import { useMutation } from "@tanstack/react-query";
import { nuevaSolicitudApi } from "@/features/nueva-solicitud/services/nueva-solicitud.api";
import { type SolicitudResumen, type TipoSolicitud } from "@/lib/types";
import { toastError, toastExito } from "@/lib/toast";
import { extractApiMessage } from "@/shared/api/extract-message";

export type SubtipoSolicitud = "CFDI_UNICO" | "MULTIPLES_CFDI" | "CONTRAPAGO" | "INVOICE";

export interface CrearSolicitudArgs {
  tipo: TipoSolicitud;
  subtipo: SubtipoSolicitud;
}

/**
 * Mutation que orquesta los 3 PATCH/POST que requiere bootstrap-ear una
 * solicitud · si cualquiera falla, el error se propaga al caller y la
 * solicitud queda en el estado parcial que el backend permita.
 *
 * El `onSuccess` recibe la solicitud creada + el tipo/subtipo elegidos · el
 * componente decide a qué ruta navegar (contrato-anualizado / cargar-cfdi /
 * cargar-zip).
 */
export function useCrearSolicitud(
  options?: {
    onSuccess?: (sol: SolicitudResumen, args: CrearSolicitudArgs) => void;
  }
) {
  return useMutation({
    mutationFn: async ({ tipo, subtipo }: CrearSolicitudArgs) => {
      const sol = await nuevaSolicitudApi.iniciar();
      await nuevaSolicitudApi.asignarTipo(sol.id, tipo);
      await nuevaSolicitudApi.asignarSubtipo(sol.id, subtipo);
      return { sol, args: { tipo, subtipo } };
    },
    onSuccess: ({ sol, args }) => {
      toastExito({ title: `Solicitud #${sol.numeroSolicitud} creada`, message: "Continúa con el siguiente paso del flujo." });
      options?.onSuccess?.(sol, args);
    },
    onError: (e) => toastError({ title: "No se pudo crear", message: extractApiMessage(e) }),
  });
}
